import api from './apiClient';

// apiClient throws the raw ApiError object (not an Error instance) for any
// non-OK response, so `error instanceof Error` here is always false and
// silently discards the backend's actual message (e.g. "El enlace de
// recuperación es inválido o ha expirado.") in favor of a generic fallback.
// Same fix pattern as companyInviteApi.ts's validateToken.
const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  const msg = (error as { error?: string })?.error;
  return msg || 'Error desconocido';
};

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
  error?: string;
  /** The account's email, so callers can pre-fill the login form. */
  email?: string;
}

export interface ValidateResetTokenResponse {
  success: boolean;
  error?: string;
}

class PasswordRecoveryApi {
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    try {
      return await api.post<ForgotPasswordResponse>(
        '/auth/forgot-password',
        { email: email.trim().toLowerCase() },
        { skipAuth: true }
      );
    } catch (error: unknown) {
      return {
        success: false,
        message: '',
        error: extractErrorMessage(error)
      };
    }
  }

  /**
   * Checks a reset/invitation token against the backend WITHOUT submitting
   * a password — used on page load so a dead/revoked/expired link shows
   * "invitation no longer valid" immediately instead of rendering a normal-
   * looking password form that only reveals it's dead once submitted.
   */
  async validateResetToken(token: string): Promise<ValidateResetTokenResponse> {
    try {
      return await api.post<ValidateResetTokenResponse>(
        '/auth/validate-reset-token',
        { token: token.trim() },
        { skipAuth: true }
      );
    } catch (error: unknown) {
      return {
        success: false,
        error: extractErrorMessage(error)
      };
    }
  }

  async resetPassword(token: string, password: string): Promise<ResetPasswordResponse> {
    try {
      return await api.post<ResetPasswordResponse>(
        '/auth/reset-password',
        { token: token.trim(), password },
        { skipAuth: true }
      );
    } catch (error: unknown) {
      return {
        success: false,
        message: '',
        error: extractErrorMessage(error)
      };
    }
  }

  // Validation helpers
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  validatePassword(password: string): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('La contraseña debe tener al menos 8 caracteres');
    }

    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('La contraseña debe contener al menos una letra minúscula');
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('La contraseña debe contener al menos una letra mayúscula');
    }

    if (!/(?=.*\d)/.test(password)) {
      errors.push('La contraseña debe contener al menos un número');
    }

    if (!/[@$!%*?&]/.test(password)) {
      errors.push('La contraseña debe contener al menos un símbolo (@$!%*?&)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  validateToken(token: string): boolean {
    return !!(token && token.length >= 10); // Basic token validation
  }
}

export const passwordRecoveryApi = new PasswordRecoveryApi();
